import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Grid, TextareaAutosize } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { Link, useHistory, useLocation } from 'react-router-dom'
import dayjs from 'dayjs'
import PageLayout from '../common/PageLayout'
import PageHeading from '../common/PageHeading'
import Divider from '../../../../components/atoms/Divider'
import SydLabel from '../../../../components/commonDesign/SydLabel'
import SydInput from '../../../../components/commonDesign/SydInput'
import SydSelect from '../../../../components/commonDesign/SydSelect'
import SydButton from '../../../../components/commonDesign/Button'
import FlexRow from '../../../../components/molecules/FlexRow'
import FlexColumn from '../../../../components/molecules/FlexColumn'
import FastAvatar from '../../../../components/molecules/FastAvatar'
import Icon from '../../../../components/atoms/Icon'
import { useAppConfig } from '../../../../redux/slices/appConfig'
import { useCreateMeeting } from '../../../../api/meetings'
import { MEETING_TYPES, SUMMARY_TYPES } from './meetingConstants'

const useStyles = makeStyles(() => ({
  modeToggle: {
    display: 'flex',
    borderRadius: 'var(--border-radius-md)',
    border: 'var(--border-neutral-thin)',
    overflow: 'hidden'
  },
  modeOption: {
    flex: 1,
    padding: '10px 16px',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 150ms ease',
    backgroundColor: 'transparent',
    color: 'var(--dai-color-text-dim)',
    border: 'none',
    '&:hover': { backgroundColor: 'var(--color-neutral-100)' }
  },
  modeOptionActive: {
    backgroundColor: 'var(--color-primary-050)',
    color: 'var(--color-primary-700)',
    fontWeight: 600,
    '&:hover': { backgroundColor: 'var(--color-primary-100)' }
  },
  summaryCard: {
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-md)',
    padding: 'var(--padding-md)',
    position: 'relative'
  },
  removeBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    cursor: 'pointer',
    opacity: 0.5,
    '&:hover': { opacity: 1 }
  }
}))

const meetingTypeOptions = MEETING_TYPES.map(t => ({ label: t.label, value: t.value }))
const summaryTypeOptions = [
  ...SUMMARY_TYPES.map(s => ({ label: s.label, value: s.type })),
  { label: 'Custom', value: '__custom__' }
]

function getNextMondayAt10AM () {
  let next = dayjs().startOf('day').day(8).hour(10).minute(0)
  if (next.diff(dayjs(), 'minute') < 15) next = next.add(7, 'day')
  return next.format('YYYY-MM-DDTHH:mm')
}

function NewMeeting () {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const clientFocus = location.state?.clientFocus ?? null
  const titleTouched = useRef(false)
  const [mode, setMode] = useState('now')
  const appConfig = useAppConfig()
  const firmName = appConfig?.pageConfig?.brand?.firmName

  const { mutateAsync: createMeeting, isLoading: processing } = useCreateMeeting()

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      meetingType: '',
      title: '',
      meetingUrl: '',
      botName: firmName ? `${firmName} Notetaker` : 'Summit Notetaker',
      joinAt: getNextMondayAt10AM(),
      summaries: SUMMARY_TYPES.filter(s => s.defaultEnabled).map(s => ({ type: s.type, prompt: s.prompt }))
    }
  })

  const { fields: summaryFields, append: addSummary, remove: removeSummary } = useFieldArray({
    control: form.control,
    name: 'summaries'
  })
  const promptTouched = useRef({})
  const [customTypeNames, setCustomTypeNames] = useState({})

  const watchedSummaries = form.watch('summaries')
  const usedTypes = useMemo(() => {
    const types = new Set()
    ;(watchedSummaries || []).forEach((s, i) => {
      if (s.type === '__custom__') {
        const custom = customTypeNames[i]
        if (custom) types.add(custom)
      } else if (s.type) {
        types.add(s.type)
      }
    })
    return types
  }, [watchedSummaries, customTypeNames])
  const availableTypeOptions = useMemo(() =>
    summaryTypeOptions.filter(o => o.value === '__custom__' || !usedTypes.has(o.value)),
  [usedTypes])

  const handleSummaryTypeChange = useCallback((index, value) => {
    form.setValue(`summaries.${index}.type`, value)
    promptTouched.current[index] = false
    if (value && value !== '__custom__') {
      const preset = SUMMARY_TYPES.find(s => s.type === value)
      if (preset) {
        form.setValue(`summaries.${index}.prompt`, preset.prompt)
      }
      setCustomTypeNames(prev => { const next = { ...prev }; delete next[index]; return next })
    }
    if (value === '__custom__') {
      form.setValue(`summaries.${index}.prompt`, '')
      setCustomTypeNames(prev => ({ ...prev, [index]: '' }))
    }
  }, [form])

  const meetingTypeValue = form.watch('meetingType')

  useEffect(() => {
    if (titleTouched.current) return
    const typeConfig = MEETING_TYPES.find(t => t.value === meetingTypeValue)
    if (typeConfig) {
      form.setValue('title', typeConfig.titleTemplate(clientFocus?.subjectLabel))
    }
  }, [meetingTypeValue, clientFocus, form])

  const rootError = form.formState.errors?.root?.message

  const onSubmit = useCallback(async (formData) => {
    const command = {
      meetingUrl: formData.meetingUrl,
      title: formData.title || null,
      meetingType: formData.meetingType || null,
      clientId: clientFocus?.subjectIdentifier ?? null,
      botName: formData.botName || 'Summit Notetaker',
      summaries: formData.summaries
        .map((s, i) => ({ ...s, type: s.type === '__custom__' ? (customTypeNames[i] || '').trim() : s.type }))
        .filter(s => s.type && s.prompt)
    }
    if (mode === 'scheduled' && formData.joinAt) {
      command.joinAt = dayjs(formData.joinAt).toISOString()
    }
    const missingCustomName = formData.summaries.some((s, i) => s.type === '__custom__' && !(customTypeNames[i] || '').trim())
    if (missingCustomName) {
      form.setError('root', { type: 'custom', message: 'Please provide a name for all custom summary types.' })
      return
    }
    try {
      form.clearErrors()
      const result = await createMeeting(command)
      history.push(`/denali-ai/meetings/${result.meetingId}`)
    } catch (err) {
      form.setError('root', { type: 'custom', message: err.message || 'Failed to create meeting.' })
    }
  }, [form, clientFocus, mode, createMeeting, history, customTypeNames])

  const handleSubmit = useCallback(async (e) => {
    form.clearErrors()
    await form.handleSubmit(onSubmit)(e)
  }, [form, onSubmit])

  return (
    <PageLayout variant='constrained'>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Link to='/denali-ai/meetings' style={{ fontSize: 'var(--font-size-xs)', color: 'var(--dai-color-text-dim)', textDecoration: 'none' }}>Meetings</Link>
          <PageHeading title='New Meeting' />
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>

        {clientFocus && (
          <Grid item xs={12}>
            <FlexRow className='gap-sm' style={{ padding: '4px 0' }}>
              <FastAvatar avatarUrl={clientFocus.avatarUrl} abbreviation={clientFocus.abbreviation} size='xs' />
              <span className='font-size-sm font-weight-bold'>{clientFocus.subjectLabel}</span>
            </FlexRow>
          </Grid>
        )}

        <Grid item xs={12}>
          <Controller
            name='meetingType'
            control={form.control}
            render={({ field }) => (
              <SydLabel label='Meeting Type'>
                <SydSelect disabled={processing} options={meetingTypeOptions} placeholder='Select a type...' {...field} />
              </SydLabel>
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name='title'
            control={form.control}
            rules={{ maxLength: { value: 255, message: 'Maximum 255 characters' } }}
            render={({ field }) => (
              <SydLabel label='Title'>
                <SydInput
                  width='inherit'
                  placeholder='Auto-generated from meeting type'
                  disabled={processing}
                  {...field}
                  onChange={e => { field.onChange(e); titleTouched.current = !!e?.target?.value }}
                />
              </SydLabel>
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <div className={classes.modeToggle}>
            <button type='button' className={`${classes.modeOption} ${mode === 'now' ? classes.modeOptionActive : ''}`} onClick={() => setMode('now')} disabled={processing}>
              Meet Now
            </button>
            <button type='button' className={`${classes.modeOption} ${mode === 'scheduled' ? classes.modeOptionActive : ''}`} onClick={() => setMode('scheduled')} disabled={processing}>
              Schedule a Meeting
            </button>
          </div>
        </Grid>

        {mode === 'scheduled' && (
          <Grid item xs={12}>
            <Controller
              name='joinAt'
              control={form.control}
              rules={{
                validate: v => {
                  if (mode !== 'scheduled') return true
                  if (!v) return 'Scheduled time is required'
                  if (dayjs(v).isBefore(dayjs().add(15, 'minute'))) return 'Must be at least 15 minutes from now'
                  return true
                }
              }}
              render={({ field, fieldState }) => (
                <SydLabel label='When' required error={fieldState.error?.message}>
                  <SydInput type='datetime-local' width='inherit' disabled={processing} {...field} />
                </SydLabel>
              )}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Controller
            name='meetingUrl'
            control={form.control}
            rules={{ required: 'Meeting URL is required', pattern: { value: /^https?:\/\/.+/i, message: 'Enter a valid URL' } }}
            render={({ field, fieldState }) => (
              <SydLabel label='Meeting URL' required error={fieldState.error?.message}>
                <SydInput width='inherit' placeholder='https://teams.microsoft.com/l/meetup-join/...' disabled={processing} {...field} />
              </SydLabel>
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name='botName'
            control={form.control}
            render={({ field }) => (
              <SydLabel label='Notetaker Name'>
                <SydInput width='inherit' disabled={processing} {...field} />
              </SydLabel>
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <FlexRow className='justify-between items-center'>
            <span className='font-size-md font-weight-bold'>Summaries</span>
            <SydButton
              variant='ghost'
              size='sm'
              onClick={() => addSummary({ type: '', prompt: '' })}
              disabled={processing || availableTypeOptions.length <= 1}
            >
              + Add Summary
            </SydButton>
          </FlexRow>
        </Grid>

        {summaryFields.map((field, index) => {
          const currentType = watchedSummaries?.[index]?.type
          const isCustom = currentType === '__custom__'
          const thisOptions = summaryTypeOptions.filter(o =>
            o.value === '__custom__' || o.value === currentType || !usedTypes.has(o.value)
          )

          return (
            <Grid item xs={12} key={field.id}>
              <div className={classes.summaryCard}>
                <div className={classes.removeBtn} onClick={() => { removeSummary(index); delete promptTouched.current[index]; setCustomTypeNames(prev => { const next = { ...prev }; delete next[index]; return next }) }}>
                  <Icon name='close' customSize={16} />
                </div>
                <FlexColumn className='gap-sm'>
                  <SydLabel label='Summary Type'>
                    <SydSelect
                      disabled={processing}
                      options={thisOptions}
                      placeholder='Select a summary type...'
                      value={currentType || ''}
                      onChange={(e) => handleSummaryTypeChange(index, e.target.value)}
                    />
                    {isCustom && (
                      <SydInput
                        width='inherit'
                        placeholder='Custom summary type'
                        disabled={processing}
                        value={customTypeNames[index] || ''}
                        onChange={(e) => setCustomTypeNames(prev => ({ ...prev, [index]: e?.target?.value ?? e }))}
                        style={{ marginTop: 8 }}
                      />
                    )}
                  </SydLabel>
                  <Controller
                    name={`summaries.${index}.prompt`}
                    control={form.control}
                    rules={{ required: 'Prompt is required' }}
                    render={({ field: f }) => (
                      <SydLabel label='Prompt'>
                        <TextareaAutosize
                          style={{ width: '100%', fontFamily: 'inherit', fontSize: 'var(--font-size-sm)' }}
                          minRows={4}
                          disabled={processing}
                          {...f}
                          onChange={(e) => { f.onChange(e); promptTouched.current[index] = true }}
                        />
                      </SydLabel>
                    )}
                  />
                </FlexColumn>
              </div>
            </Grid>
          )
        })}

        {rootError && (
          <Grid item xs={12}>
            <div className='font-size-sm p-md' style={{ backgroundColor: 'var(--color-danger-500)', color: 'var(--color-danger-050)', borderRadius: 'var(--border-radius-lg)' }}>
              {rootError}
            </div>
          </Grid>
        )}

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <FlexRow className='gap-md justify-end'>
            <SydButton size='lg' variant='ghost' onClick={() => history.go(-1)} disabled={processing}>Cancel</SydButton>
            <SydButton size='lg' variant='primary' onClick={handleSubmit} processing={processing}>
              {mode === 'scheduled' ? 'Schedule Meeting' : 'Meet Now'}
            </SydButton>
          </FlexRow>
        </Grid>
      </Grid>
    </PageLayout>
  )
}

export default NewMeeting
